import { Fragment } from "react";
import { formatShiftTime } from "@/helpers/allocations";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { WorkerAllocation } from "@/types/allocations";
import { ALLOCATION_SKILLS, SKILL_IDS } from "@/constants/allocations";
import { Clock, Pencil } from "lucide-react";
import { Td } from "./TableComponents";

interface SkillTypeGroup {
  typeName: string;
  skillNames: string[];
}

interface WorkerRowProps {
  worker: WorkerAllocation;
  skillsByType: SkillTypeGroup[];
  onReceivingLeadsChange: (receivingLeads: boolean) => void;
  onEdit: (userId: number) => void;
}

export const WorkerRow = ({ worker, skillsByType, onReceivingLeadsChange, onEdit }: WorkerRowProps) => {
  return (
    <tr className="border-b transition-colors">
      <Td className="bg-white pt-4 pr-0">
        <Switch checked={worker.receivingLeads} onCheckedChange={onReceivingLeadsChange} size="sm" />
      </Td>
      <Td className="bg-white whitespace-nowrap">
        <div className="flex flex-col">
          <span className="font-medium">{worker.name}</span>
          <span className="text-muted-foreground text-xs">{worker.department}</span>
        </div>
      </Td>
      <Td className="text-center text-sm whitespace-nowrap">
        {worker.shiftStart && worker.shiftEnd ? (
          <div className="flex items-center justify-center gap-2">
            <Clock className="text-muted-foreground" size={14} />
            {formatShiftTime(worker.shiftStart)} - {formatShiftTime(worker.shiftEnd)}
          </div>
        ) : (
          "-"
        )}
      </Td>
      <Td>
        <div className="flex flex-wrap gap-2">
          {SKILL_IDS.map((skillId) => (
            <Badge className="bg-slate-50" key={skillId} variant="outline">
              <span className="text-slate-600">{ALLOCATION_SKILLS[skillId]}:</span>
              <span className="font-semibold">{worker.allocationSkills[skillId] ?? 0}</span>
            </Badge>
          ))}
        </div>
      </Td>
      <Td>
        <div className="grid grid-cols-[max-content_1fr] gap-2 text-xs">
          {skillsByType.map((group) => (
            <Fragment key={group.typeName}>
              <div className="text-muted-foreground font-medium">{group.typeName}:</div>
              <div>{group.skillNames.join(", ")}</div>
            </Fragment>
          ))}
        </div>
      </Td>
      <Td>
        <Button variant="outline" size="sm" onClick={() => onEdit(worker.userId)}>
          <Pencil className="size-3.5" />
          Edit
        </Button>
      </Td>
    </tr>
  );
};
